import { HEARTBEAT_STALE_MINUTES, type StatusResponse } from '@brimstone/shared';
import { Link } from 'react-router';
import { useStatus } from '../lib/queries';
import { formatAge, formatClock, minutesSince } from '../lib/time';

type Line = { text: string; tone: 'muted' | 'danger'; to?: string };

function files(n: number): string {
  return `${n} ${n === 1 ? 'file' : 'files'}`;
}

/** The header status (SPEC §9): sync time and file count, or what's wrong. */
export function describeStatus(status: StatusResponse, now = Date.now()): Line {
  const errors = status.parseErrors.length;
  if (errors > 0) {
    return {
      text: `${errors} parse ${errors === 1 ? 'error' : 'errors'}`,
      tone: 'danger',
      to: '/settings',
    };
  }
  const w = status.watcher;
  if (!w?.lastHeartbeatAt) return { text: 'Watcher not paired', tone: 'muted', to: '/settings' };
  if (minutesSince(w.lastHeartbeatAt, now) > HEARTBEAT_STALE_MINUTES) {
    return {
      text: `Watcher offline · seen ${formatAge(w.lastHeartbeatAt, now)}`,
      tone: 'danger',
      to: '/settings',
    };
  }
  if (!w.lastUploadAt) return { text: 'Watcher online · no files yet', tone: 'muted' };
  return {
    text: `Watcher synced ${formatClock(w.lastUploadAt, new Date(now))} · ${files(status.rawFiles)}`,
    tone: 'muted',
  };
}

export function SyncStatus() {
  const { data, isError } = useStatus();
  const line: Line = data
    ? describeStatus(data)
    : isError
      ? { text: 'Status unavailable', tone: 'danger' }
      : { text: 'Checking watcher…', tone: 'muted' };

  const className = `font-mono text-secondary ${line.tone === 'danger' ? 'text-danger' : 'text-muted'}`;
  return line.to ? (
    <Link to={line.to} className={`${className} no-underline hover:underline`}>
      {line.text}
    </Link>
  ) : (
    <span className={className}>{line.text}</span>
  );
}
